import { cloneDeep } from 'lodash';

import { ChatMessage } from '~/lib/types/chat';

import ChatUtil from './chat.utils';
import StringUtil from '../string.util';

export type SearchResult = {
  foundMessageIds: number[];
  updatedMessages: ChatMessage[];
};

export const performSearch = (
  messages: ChatMessage[],
  searchKeyword: string,
): SearchResult => {
  const result: SearchResult = {
    foundMessageIds: [],
    updatedMessages: cloneDeep(messages ?? []),
  };

  if (messages && searchKeyword) {
    // Search count based on number of messages not number of occurrences
    const lowerKeyword = searchKeyword.toLowerCase();
    const foundMessages = messages.filter(
      (x) => x.message?.toLowerCase().includes(lowerKeyword),
    );

    // Search within replies in case of threaded messages
    for (const message of messages) {
      if (
        message.thread?.some(({ message }) =>
          message.toLowerCase().includes(lowerKeyword),
        )
      ) {
        foundMessages.push(message);
      }
    }

    // Search should start from oldest message to newest message
    if (foundMessages.length > 0)
      result.foundMessageIds = [
        ...new Set(
          ChatUtil.sortByCreatedDate(foundMessages).map((x) => x.messageId),
        ),
      ];

    // Highlight searched keyword
    result.updatedMessages = messages.map((item) => {
      return {
        ...item,
        message: ChatUtil.replaceAll(
          item.message,
          searchKeyword,
          `<mark>#</mark>`,
        ),
        thread: (item.thread ?? []).map((reply) => {
          return {
            ...reply,
            message: ChatUtil.replaceAll(
              reply.message,
              searchKeyword,
              `<mark>#</mark>`,
            ),
          };
        }),
      };
    });
  }

  return result;
};

export const renderNewLine = (messages: ChatMessage[]): ChatMessage[] => {
  return (messages ?? []).map((item) => {
    return {
      ...item,
      message: StringUtil.replaceAll(item.message, '\n', '<br/>'),
    };
  });
};

// URL serialization leaves apostrophes unencoded, so an unescaped value would
// close the single-quoted attribute and inject new ones. The link text needs
// the same treatment: it is the raw message segment, not a serialized URL.
const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/'/g, '&#39;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

export const anchorTag = (url: string, segment: string) =>
  `<a href='${escapeHtml(url)}' target='_blank' rel='noreferrer noopener'>${escapeHtml(segment)}</a>`;

export const renderLinks = (messages: ChatMessage[]): ChatMessage[] => {
  return (messages ?? [])?.map((item) => {
    const segments = item.message?.split('<br/>').join(` <br/> `).split(' ');

    if (segments?.length > 0) {
      const joinedSegments = segments
        .map((segment) => {
          if (ChatUtil.isValidUrl(segment)) {
            return anchorTag(ChatUtil.getUrl(segment), segment);
          }

          return segment;
        })
        .join(' ');

      return { ...item, message: joinedSegments };
    }
    return item;
  });
};
